<template>
  <div class="themed-multiselect">
    <ThemedButton
      ref="trigger"
      variant="outline"
      @click.stop="toggleDropdown"
      type="button"
      class="multiselect-trigger w-full"
      :disabled="disabled"
    >
      <span v-if="selectedValues.length === 0" class="placeholder">{{ placeholder }}</span>
      <span v-else class="selected-count">{{ selectedValues.length }} selected</span>
      <ThemeIcon
        icon="chevron-down"
        type="icons"
        :classes="isOpen ? 'dropdown-arrow open' : 'dropdown-arrow'"
      />
    </ThemedButton>
  </div>
</template>

<script>
import Vue from 'vue';
import ThemeIcon from './ThemeIcon.vue';
import ThemedButton from './ThemedButton.vue';
import ThemedCheckbox from './ThemedCheckbox.vue';

export default {
  name: 'ThemedMultiSelect',

  components: { ThemeIcon, ThemedButton },

  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, required: true, default: () => [] },
    placeholder: { type: String, default: 'Select options...' },
    disabled: { type: Boolean, default: false },
    grouped: { type: Boolean, default: false }
  },
  created() {

    // Vue does not proxy keys beginning with an underscore, so these were

    // never reactive state. They are plain instance properties.

    this._dropdownVm = null;

    this._outsideHandler = null;

  },
  data() {
    return {
      isOpen: false,
    };
  },

  computed: {
    selectedValues() {
      return this.value || [];
    },

    normalizedOptions() {
      if (this.grouped) return this.options;
      return this.options.map(option => {
        if (typeof option === 'string' || typeof option === 'number') {
          return { label: String(option), value: option };
        }
        return {
          label: option.label || option.text || option.name || String(option.value),
          value: option.value !== undefined ? option.value : option
        };
      });
    }
  },

  beforeDestroy() {
    this._destroyDropdown();
  },

  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      if (this.isOpen) {
        this._destroyDropdown();
      } else {
        this._openDropdown();
      }
    },

    _openDropdown() {
      this.isOpen = true;

      const rect = this.$refs.trigger.$el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 200;
      const maxH = openUpward
        ? Math.min(320, rect.top - 8)
        : Math.min(320, spaceBelow - 8);

      const parent = this;
      const DropdownComponent = Vue.extend({
        components: { ThemedCheckbox },
        data() {
          return {
            style: {
              position: 'fixed',
              top: openUpward ? 'auto' : (rect.bottom + 4) + 'px',
              bottom: openUpward ? (window.innerHeight - rect.top + 4) + 'px' : 'auto',
              left: rect.left + 'px',
              width: rect.width + 'px',
              maxHeight: Math.max(maxH, 80) + 'px',
              overflowY: 'auto',
              overflowX: 'hidden',
              zIndex: '999999',
              background: 'var(--card, #1a2035)',
              border: '1px solid var(--border, rgba(255,255,255,0.15))',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            }
          };
        },
        render(h) {
          const grouped = parent.grouped;
          const options = parent.normalizedOptions;
          const selected = parent.selectedValues;

          const isSelected = (val) => selected.includes(val);

          const renderOption = (option) => h('label', {
            key: option.value,
            staticClass: 'checkbox-option',
            on: { mousedown: (e) => e.preventDefault() }
          }, [
            h('ThemedCheckbox', {
              props: { value: isSelected(option.value), label: option.label },
              on: { input: () => parent.toggleOption(option.value) }
            })
          ]);

          let content;
          if (grouped) {
            content = options.map(group => h('div', { key: group.name, staticClass: 'category-group' }, [
              h('div', { staticClass: 'category-label' }, group.name),
              ...group.options.map(renderOption)
            ]));
          } else {
            content = options.map(renderOption);
          }

          return h('div', { style: this.style, on: { mousedown: (e) => e.stopPropagation() } }, [
            h('div', { staticClass: 'multiselect-actions' }, [
              h('button', { attrs: { type: 'button' }, staticClass: 'multiselect-action-btn', on: { mousedown: (e) => { e.preventDefault(); parent.selectAll(); } } }, 'Select All'),
              h('button', { attrs: { type: 'button' }, staticClass: 'multiselect-action-btn', on: { mousedown: (e) => { e.preventDefault(); parent.clearAll(); } } }, 'Clear'),
            ]),
            ...content
          ]);
        }
      });

      const vm = new DropdownComponent().$mount();
      document.body.appendChild(vm.$el);
      this._dropdownVm = vm;

      // Close on outside click
      this._outsideHandler = (e) => {
        if (!this.$el.contains(e.target) && !vm.$el.contains(e.target)) {
          this._destroyDropdown();
        }
      };
      setTimeout(() => document.addEventListener('mousedown', this._outsideHandler), 0);
    },

    _destroyDropdown() {
      this.isOpen = false;
      if (this._dropdownVm) {
        document.body.removeChild(this._dropdownVm.$el);
        this._dropdownVm.$destroy();
        this._dropdownVm = null;
      }
      if (this._outsideHandler) {
        document.removeEventListener('mousedown', this._outsideHandler);
        this._outsideHandler = null;
      }
    },

    isSelected(value) {
      return this.selectedValues.includes(value);
    },

    toggleOption(value) {
      const newValues = [...this.selectedValues];
      const index = newValues.indexOf(value);
      if (index > -1) {
        newValues.splice(index, 1);
      } else {
        newValues.push(value);
      }
      this.$emit('input', newValues);
      // Re-render dropdown to reflect new selection
      if (this._dropdownVm) this._dropdownVm.$forceUpdate();
    },

    selectAll() {
      const allValues = [];
      if (this.grouped) {
        this.normalizedOptions.forEach(g => g.options.forEach(o => allValues.push(o.value)));
      } else {
        this.normalizedOptions.forEach(o => allValues.push(o.value));
      }
      this.$emit('input', allValues);
      if (this._dropdownVm) this._dropdownVm.$forceUpdate();
    },

    clearAll() {
      this.$emit('input', []);
      if (this._dropdownVm) this._dropdownVm.$forceUpdate();
    }
  }
};
</script>
